// iOS Mail-style list of audit events: generous padding, dot indicator
// for action tone, primary line is table.name, secondary line carries
// the action label, timestamp, and actor id. A trailing chevron hints
// the row drills into a detail view.

import type { AuditEvent } from "../data/audit.ts";
import { ActionDot, formatCt } from "./utils.tsx";

export type LoadState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "loaded"; events: AuditEvent[]; nextCursor: string | null }
  | { kind: "unauthorized" }
  | { kind: "error"; status: number; error: string };

export function AuditList({
  state,
  selectedId,
  onSelect,
  onLoadMore,
}: {
  state: LoadState;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onLoadMore: () => void;
}) {
  if (state.kind === "loading" || state.kind === "idle") {
    return <EmptyState text="Loading…" />;
  }
  if (state.kind === "unauthorized") {
    return (
      <EmptyState>
        <p className="text-[15px] text-slate-600">Not available.</p>
        <a className="mt-2 inline-block text-[15px] text-[#052460] font-medium" href="#/sign-in">
          Sign in
        </a>
      </EmptyState>
    );
  }
  if (state.kind === "error") {
    return <EmptyState text={`${state.status} · ${state.error}`} tone="error" />;
  }
  if (state.events.length === 0) {
    return <EmptyState text="No events match." />;
  }
  return (
    <>
      <ul className="px-2 md:px-6">
        {state.events.map((e) => (
          <li key={e.id}>
            <button
              type="button"
              onClick={() => onSelect(e.id)}
              className={`w-full text-left px-3 md:px-2 py-3.5 flex items-center gap-3 rounded-xl ${
                selectedId === e.id ? "bg-slate-200/60" : "active:bg-slate-200/40"
              }`}
            >
              <ActionDot action={e.action} />
              <div className="flex-1 min-w-0">
                <p className="text-[15px] text-slate-900 font-medium font-mono truncate">
                  {e.tableSchema}.{e.tableName}
                </p>
                <p className="text-[13px] text-slate-500 truncate">
                  {e.action} · {formatCt(e.occurredAt)}
                  {e.actorId ? ` · ${e.actorId}` : ""}
                </p>
              </div>
              <ChevronRight />
            </button>
          </li>
        ))}
      </ul>
      {state.nextCursor && (
        <div className="px-5 md:px-8 py-4">
          <button
            type="button"
            onClick={onLoadMore}
            className="text-[15px] text-[#052460] font-medium"
          >
            Load older
          </button>
        </div>
      )}
    </>
  );
}

function EmptyState({
  text,
  children,
  tone,
}: {
  text?: string;
  children?: React.ReactNode;
  tone?: "error";
}) {
  const cls = tone === "error" ? "text-red-600" : "text-slate-400";
  return (
    <div className="px-5 md:px-8 py-10 text-center">
      {children ?? <p className={`text-[15px] ${cls}`}>{text}</p>}
    </div>
  );
}

function ChevronRight() {
  return (
    <svg
      width="8"
      height="14"
      viewBox="0 0 8 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="text-slate-400 shrink-0"
    >
      <path d="M1 1l6 6-6 6" />
    </svg>
  );
}
